/**
 * Idle-safety poll for the server→client message bus: calls the app's
 * `GET /messages/ping` every ~45s while the tab is visible and `isActive()`
 * holds. The ping's only purpose is to carry queued `meta.messages` for an
 * otherwise-idle client — it is the one periodic call the bus needs, and it
 * replaces per-feature polls (e.g. the Feedback badge).
 *
 * See docs/FeatureDesigns/duels-invite-smoothing-plan.md.
 */

import { useEffect, useRef } from 'react';

const PING_MS = 45_000;

export function useMessagePing(ping: () => Promise<unknown>, isActive: () => boolean): void {
    // Latest callbacks without restarting the timer on every render.
    const pingRef = useRef(ping);
    const activeRef = useRef(isActive);
    pingRef.current = ping;
    activeRef.current = isActive;

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const tick = async () => {
            if (document.visibilityState === 'visible' && activeRef.current()) {
                try { await pingRef.current(); } catch { /* ignore */ }
            }
            timer = setTimeout(tick, PING_MS);
        };
        timer = setTimeout(tick, PING_MS);
        return () => { if (timer) clearTimeout(timer); };
    }, []);
}
